/**
 * SessionHistoryCard — Displays domain testing history for returning users
 * with stats, page breakdown, and action buttons.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import type { SessionHistorySummary, TestingAction } from '../types';

interface SessionHistoryCardProps {
  history: SessionHistorySummary;
  actions: TestingAction[];
  onSelectAction: (action: string) => void;
}

const SessionHistoryCard: React.FC<SessionHistoryCardProps> = ({
  history,
  actions,
  onSelectAction,
}) => {
  const totalPages = history.total_pages_tested || 1;
  const passRate = totalPages > 0
    ? Math.round((history.pages_passed / totalPages) * 100)
    : 0;

  const accentColor = passRate >= 80 ? '#4caf50' : passRate >= 50 ? '#ff9800' : '#f44336';

  const lastTestedAgo = history.last_session_at
    ? getTimeAgo(history.last_session_at)
    : 'Unknown';

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `4px solid ${accentColor}`,
        mb: 2,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {history.domain}
          </Typography>
          <Chip
            label={`Run #${history.total_sessions}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {history.total_pages_tested} pages tested, {history.pages_passed} passed,{' '}
          {history.pages_failed} failed — last run {lastTestedAgo}
          {history.tms_test_cases_count > 0 && (
            <> | {history.tms_test_cases_count} TMS test cases</>
          )}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption">Pass Rate</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {passRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={passRate}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: accentColor,
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {history.page_details.length > 0 && (
          <TableContainer sx={{ maxHeight: 200, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Pass Rate</TableCell>
                  <TableCell align="right">Last Tested</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.page_details.map((page, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {page.page_title || page.url}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {page.status === 'passed' && (
                        <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                      )}
                      {page.status === 'failed' && (
                        <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />
                      )}
                      {page.status === 'mixed' && (
                        <WarningIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{page.pass_rate}%</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {page.last_tested_at ? getTimeAgo(page.last_tested_at) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {actions.map((action) => (
            <Button
              key={action.id}
              variant={action.id === 'retest_failed' ? 'contained' : 'outlined'}
              size="small"
              disabled={!action.enabled}
              onClick={() => onSelectAction(action.id)}
              sx={{ textTransform: 'none' }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

function getTimeAgo(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

export default SessionHistoryCard;
